import type { Expression, LValue, Statement } from '../parser/ast.js';

/**
 * Επιστρέφει το μέγιστο πλήθος δεικτών (arity) με το οποίο χρησιμοποιείται
 * το `name` ως πίνακας οπουδήποτε μέσα στις εντολές — 0 αν δεν
 * χρησιμοποιείται ποτέ με δείκτη (άρα είναι απλή μεταβλητή).
 *
 * Χρησιμοποιείται για το "Δεδομένα // ... //" της Ψευδογλώσσας: αφού δεν
 * υπάρχουν τυπικές δηλώσεις, ο μόνος τρόπος να ξέρουμε αν ένα όνομα εκεί
 * αναφέρεται σε πίνακα ή σε απλή μεταβλητή είναι να δούμε πώς πράγματι
 * χρησιμοποιείται στο σώμα του αλγορίθμου (επιβεβαιωμένο από πραγματικό
 * διερμηνευτή: `Δεδομένα // Α //` + χρήση `Α[ι]` στο σώμα → ζητά πρώτα
 * διαστάσεις, μετά κάθε στοιχείο ξεχωριστά, αντί για ένα απλό prompt).
 */
export function detectArrayArity(name: string, statements: readonly Statement[]): number {
  let arity = 0;
  const note = (n: number): void => {
    if (n > arity) arity = n;
  };

  function visitExpr(expr: Expression): void {
    switch (expr.kind) {
      case 'ArrayAccess':
        if (expr.array.name === name) note(expr.indices.length);
        for (const idx of expr.indices) visitExpr(idx);
        return;
      case 'BinaryExpression':
        visitExpr(expr.left);
        visitExpr(expr.right);
        return;
      case 'UnaryExpression':
        visitExpr(expr.operand);
        return;
      case 'FunctionCall':
        for (const arg of expr.args) visitExpr(arg);
        return;
      case 'Identifier':
      case 'NumberLiteral':
      case 'StringLiteral':
      case 'BooleanLiteral':
        return;
    }
  }

  function visitLValue(target: LValue): void {
    if (target.kind !== 'ArrayAccess') return;
    if (target.array.name === name) note(target.indices.length);
    for (const idx of target.indices) visitExpr(idx);
  }

  function visitStatements(stmts: readonly Statement[]): void {
    for (const stmt of stmts) visitStatement(stmt);
  }

  function visitStatement(stmt: Statement): void {
    switch (stmt.kind) {
      case 'AssignmentStatement':
        visitLValue(stmt.target);
        visitExpr(stmt.value);
        return;
      case 'ReadStatement':
        for (const t of stmt.targets) visitLValue(t);
        return;
      case 'WriteStatement':
        for (const v of stmt.values) visitExpr(v);
        return;
      case 'SwapStatement':
        visitLValue(stmt.left);
        visitLValue(stmt.right);
        return;
      case 'IfStatement':
        visitExpr(stmt.condition);
        visitStatements(stmt.thenBranch);
        for (const branch of stmt.elseIfBranches) {
          visitExpr(branch.condition);
          visitStatements(branch.body);
        }
        if (stmt.elseBranch !== null) visitStatements(stmt.elseBranch);
        return;
      case 'ForStatement':
        visitExpr(stmt.from);
        visitExpr(stmt.to);
        if (stmt.step !== null) visitExpr(stmt.step);
        visitStatements(stmt.body);
        return;
      case 'WhileStatement':
        visitExpr(stmt.condition);
        visitStatements(stmt.body);
        return;
      case 'RepeatUntilStatement':
        visitStatements(stmt.body);
        visitExpr(stmt.condition);
        return;
      case 'SwitchStatement':
        visitExpr(stmt.discriminant);
        for (const c of stmt.cases) {
          for (const v of c.values) {
            if (v.kind === 'CaseRange') {
              visitExpr(v.from);
              visitExpr(v.to);
            } else if (v.kind === 'CaseComparison') {
              visitExpr(v.value);
            } else {
              visitExpr(v);
            }
          }
          visitStatements(c.body);
        }
        if (stmt.elseBody !== null) visitStatements(stmt.elseBody);
        return;
      case 'CallStatement':
        for (const a of stmt.args) visitExpr(a);
        return;
    }
  }

  visitStatements(statements);
  return arity;
}
